import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material'
import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CustomExplain } from '../components/CustomExplain'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { loadStatus } from '../constants/loadStatus'
import { loginErrorIntervalMinutes, loginErrorLimitTimes } from '../constants/loginLimit'
import { errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC003AuthMfaCodeCreate } from '../hooks/useC003AuthMfaCodeCreate'
import { useC004AuthMfaVerify } from '../hooks/useC004AuthMfaVerify'
import { ReactComponent as CotopatImage } from '../images/cotopat-logo.svg'
import { MfaSchema } from '../schema/mfaSchema'
import { LanguageContext } from '../util/LanguageContext'
import { VersionContext } from '../util/VersionContext'

// 2段階認証画面
export const Mfa = () => {
  // hooks
  const auth = useAuth()
  const c003 = useC003AuthMfaCodeCreate()
  const c004 = useC004AuthMfaVerify()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')
  const [loading, setLoading] = useState(loadStatus.NONE)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isDisabled, setIsDisabled] = useState(true)
  const [inputValue, setInputValue] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const navigate = useNavigate()

  const { version } = useContext(VersionContext)

  const validate = async (value: string) => {
    try {
      await MfaSchema.validate(value)
      setErrorMessage('')
      setIsDisabled(false)
    } catch (err: any) {
      setErrorMessage(err.message)
      setIsDisabled(true)
    }
  }
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
    validate(event.target.value)
  }
  const onClickCancel = () => {
    navigate('/login')
  }
  const onRecreateCode = async () => {
    setLoading(loadStatus.LOADING)
    const resultMfa = await c003.execAuthMfaCodeCreate(auth.loginId)
    if (resultMfa.success) {
      setErrorDialog('email_resend_msg')
      setOpenDialog(true)
      setLoading(loadStatus.NONE)
    } else {
      setErrorDialog(resultMfa.message!)
      setOpenDialog(true)
    }
  }
  const handleSubmit = async () => {
    setLoading(loadStatus.LOADING)
    // ログイン失敗回数変数
    const loginIdErrCntName = auth.loginId + 'ErrCntMFA'
    // ログイン失敗時刻変数
    const loginIdErrTimeName = auth.loginId + 'ErrTimeMFA'
    // ローカルストレージからログイン失敗回数とログイン失敗時刻を取得する
    const loginIdErrCnt = localStorage.getItem(loginIdErrCntName)
    const loginIdErrTime = localStorage.getItem(loginIdErrTimeName)
    let errorCount = 0
    const currentTime: number = Date.now()

    if (loginIdErrCnt) {
      errorCount = Number.parseInt(loginIdErrCnt, 10)
    }
    if (loginIdErrTime) {
      const savedTime: number = Number.parseInt(loginIdErrTime, 10)
      const thresholdTime: number = savedTime + loginErrorIntervalMinutes * 60 * 1000
      if (currentTime > thresholdTime) {
        errorCount = 0
      }
    }
    // ログイン失敗回数を超過している場合
    if (errorCount >= loginErrorLimitTimes) {
      console.log(`ログイン失敗回数超過：${errorCount}`)
      setErrorDialog(errorMsg.AUTH_LIMIT_ERROR)
      setOpenDialog(true)
      return
    }
    // サインイン
    const result = await c004.execAuthMfaVerify(inputValue)
    if (result.success) {
      // ログイン成功時は、ログイン制限チェック関数があれば削除する
      if (loginIdErrCnt) {
        localStorage.removeItem(loginIdErrCntName)
      }
      if (loginIdErrTime) {
        localStorage.removeItem(loginIdErrTimeName)
      }
      /*1:窓口(Customer contact)*/
      if (auth.role === 1) {
        navigate('/phrase')
        /*3:キッティング業者(RHQ Member/Kitting operator), 2:デバイス管理者(RHQ Administrator)*/
        // 初期表示は顧客情報管理(デバイス一覧)画面を表示
        // RHQ Administrator、京セラの権限の場合はSideMenuに管理者情報メニューも表示される
      } else {
        navigate('/device')
      }
    } else {
      // ログイン失敗ルート
      errorCount++
      // ログイン失敗回数と失敗時刻を書込み
      localStorage.setItem(loginIdErrCntName, errorCount.toString())
      localStorage.setItem(loginIdErrTimeName, currentTime.toString())
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // 起動時
  useEffect(() => {
    setIsDisabled(inputValue === '')
  }, [inputValue])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const onDialogClosekHandler = async () => {
    setErrorDialog('')
    setOpenDialog(false)
    setLoading(loadStatus.NONE)
  }

  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]

  let resendButtonJsx: JSX.Element
  switch (baseLanguage) {
    case 'it':
    case 'nl':
    case 'tr':
      resendButtonJsx = (
        <Button
          type="submit"
          color="primary"
          variant="contained"
          sx={{
            mt: '10px',
            mb: '60px',
            width: '300px',
            height: '49px',
            border: '2px solid #282828',
            borderRadius: '10px',
            boxShadow: 0,
            textAlign: 'center',
            font: 'normal normal bold 16px/29px Noto Sans CJK JP',
            letterSpacing: '0px',
            color: '#282828',
            '&:hover': {
              background: '#282828 0% 0% no-repeat padding-box',
              opacity: 0.8,
              boxShadow: 0,
            },
          }}
          style={{ backgroundColor: '#F4F4F4' }}
          onClick={onRecreateCode}
        >
          {translation['MFA_code_resubmit']}
        </Button>
      )
      break
    default:
      resendButtonJsx = (
        <Button
          type="submit"
          color="primary"
          variant="contained"
          sx={{
            mt: '10px',
            mb: '60px',
            width: '300px',
            height: '49px',
            border: '2px solid #282828',
            borderRadius: '10px',
            boxShadow: 0,
            textAlign: 'center',
            font: 'normal normal bold 21px/29px Noto Sans CJK JP',
            letterSpacing: '0px',
            color: '#282828',
            '&:hover': {
              background: '#282828 0% 0% no-repeat padding-box',
              opacity: 0.8,
              boxShadow: 0,
            },
          }}
          style={{ backgroundColor: '#F4F4F4' }}
          onClick={onRecreateCode}
        >
          {translation['MFA_code_resubmit']}
        </Button>
      )
      break
  }

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog open={openDialog} text={errorDialog} onClick={onDialogClosekHandler} />
      )}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
        }}
      >
        {(c004.loading === loadStatus.LOADING ||
          c003.loading === loadStatus.LOADING ||
          auth.loading === loadStatus.LOADING ||
          loading === loadStatus.LOADING) && <CircularProgress />}
      </Box>
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'auto',
            width: '100%',
            marginTop: '80px',
          }}
        >
          <Paper
            elevation={5}
            sx={{
              width: '787px',
              boxShadow: '0px 2px 2px #00000026',
              borderRadius: '10px',
            }}
          >
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
              <Stack direction="column">
                <Box mt="111px" display="flex" justifyContent="center">
                  <CotopatImage width="172px" height="44px" />
                </Box>
                <Typography
                  align="center"
                  sx={{
                    mt: '80px',
                    mr: '100px',
                    ml: '100px',
                    top: '315px',
                    left: '759px',
                    font: 'normal normal normal 21px/29px Noto Sans JP',
                  }}
                >
                  <CustomExplain>{translation['mfa_msg']}</CustomExplain>
                </Typography>
                <Box display="flex" justifyContent="center">
                  <Box>
                    <Typography
                      sx={{
                        mt: '60px',
                        width: '400px',
                        font: 'normal normal normal 21px/29px Noto Sans JP',
                      }}
                    >
                      {translation['mfa_code']}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="center">
                  <TextField
                    label=""
                    variant="outlined"
                    value={inputValue}
                    error={!!errorMessage}
                    helperText={errorMessage ? translation[errorMessage] : ' '}
                    inputRef={inputRef}
                    onChange={handleInputChange}
                    InputProps={{
                      sx: {
                        font: 'normal normal normal 21px/34px BIZ UDPGothic',
                        borderRadius: '10px',
                        height: '72px',
                        width: '400px',
                        pl: '8px',
                        background: '#F4F4F4',
                      },
                    }}
                    sx={{
                      mt: '5px',
                      width: '402px',
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: errorMessage ? 'red' : '#282828',
                        },
                      },
                    }}
                  />
                </Box>
              </Stack>
              <Box mt={3} display="flex" gap="40px" marginBottom="50px">
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{
                    width: '184px',
                    height: '49px',
                    border: '2px solid #282828',
                    borderRadius: '10px',
                    boxShadow: 0,
                    textAlign: 'center',
                    font: 'normal normal bold 21px/29px Noto Sans CJK JP',
                    letterSpacing: '0px',
                    color: '#282828',
                    '&:hover': {
                      background: '#282828 0% 0% no-repeat padding-box',
                      opacity: 0.8,
                      boxShadow: 0,
                    },
                  }}
                  style={{ backgroundColor: '#F4F4F4' }}
                  onClick={onClickCancel}
                >
                  {translation['button_cancel']}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{
                    width: '184px',
                    height: '49px',
                    borderRadius: '10px',
                    boxShadow: 0,
                    textAlign: 'center',
                    font: 'normal normal bold 21px/29px Noto Sans CJK JP',
                    letterSpacing: '0px',
                    color: '#FFFFFF',
                    '&:hover': {
                      background: '#282828 0% 0% no-repeat padding-box',
                      opacity: 0.8,
                      boxShadow: 0,
                    },
                  }}
                  disabled={isDisabled}
                  style={{
                    backgroundColor: isDisabled ? '#F4F4F4' : '#282828',
                  }}
                  onClick={handleSubmit}
                >
                  {translation['button_login']}
                </Button>
              </Box>
              <Box display="flex" justifyContent="center">
                {resendButtonJsx}
              </Box>
              <Box display="flex" justifyContent="center" alignItems="center" mb="30px">
                <Typography
                  mb="2px"
                  sx={{
                    font: 'normal normal normal 16px/24px Noto Sans JP',
                    textAlign: 'left',
                    letterSpacing: '0px',
                    color: '#282828',
                  }}
                >
                  {translation['copyright_info']}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: '16px',
          right: '16px',
          font: 'normal normal normal 12px/12px Noto Sans JP',
          color: '#999999',
        }}
      >
        {version ? `${translation['app_version']}`.replace('{version}', version) : ''}
      </Box>
    </>
  )
}
